import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { get, getErrorMessage } from '../api/client';
import { STATUS_LABELS, activityDate, buildProgressCsv, dashboardTotals, filterPeople } from '../utils/leadersSurveyAdmin';
import './SurveyAdmin.css';

export function SurveyProgressView({ roster }) {
  const [moduleKey, setModuleKey] = useState('');
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');
  const modules = roster?.modules || [];
  const visible = useMemo(
    () => filterPeople(roster?.people || [], { moduleKey, status, query }),
    [roster, moduleKey, status, query]
  );
  const totals = dashboardTotals(visible);

  const exportStatus = () => {
    const csv = buildProgressCsv(visible);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'survey-progress.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="survey-admin">
      <PageHeader title="Survey progress" section="leaders" />
      <p className="survey-admin-lead">
        Status for people who have modules assigned. This view does not include their answers.
      </p>
      <p className="survey-admin-links">
        <Link to="/admin/survey">Edit assignments</Link>
        {' · '}
        <Link to="/admin/survey/results">View results</Link>
      </p>
      <dl className="survey-admin-totals">
        <div><dt>Tasked</dt><dd>{totals.tasked}</dd></div>
        <div><dt>Not started</dt><dd>{totals.notStarted}</dd></div>
        <div><dt>In progress</dt><dd>{totals.inProgress}</dd></div>
        <div><dt>Complete</dt><dd>{totals.completed}</dd></div>
        <div><dt>Overall</dt><dd>{totals.percent}%</dd></div>
      </dl>
      <div className="survey-admin-filters">
        <label>
          <span>Module</span>
          <select value={moduleKey} onChange={(event) => setModuleKey(event.target.value)}>
            <option value="">All modules</option>
            {modules.map((mod) => (
              <option key={mod.key} value={mod.key}>{mod.key}. {mod.title}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Status</span>
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">All statuses</option>
            <option value="not_started">Not started</option>
            <option value="in_progress">In progress</option>
            <option value="complete">Complete</option>
          </select>
        </label>
        <label className="survey-admin-search">
          <span>Search</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Name or email"
          />
        </label>
      </div>
      <button type="button" className="survey-admin-export" onClick={exportStatus}>
        Export status
      </button>
      {visible.length === 0 && <p>No one matches these filters.</p>}
      <ul className="survey-admin-list">
        {visible.map((person) => (
          <li key={person.userId} className="survey-admin-card">
            <div className="survey-admin-card-head">
              <h2>{person.name}</h2>
              <p className="survey-admin-status">{person.rollup.statusLabel}</p>
            </div>
            {person.email && <p className="survey-admin-email">{person.email}</p>}
            <p className="survey-admin-overall">{person.rollup.percent}% answered</p>
            <div
              className="survey-admin-bar"
              role="progressbar"
              aria-valuenow={person.rollup.percent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${person.name} progress`}
            >
              <div className="survey-admin-bar-fill" style={{ width: `${person.rollup.percent}%` }} />
            </div>
            <ul className="survey-admin-module-stats">
              {person.rollup.modules.map((key) => {
                const row = person.modules[key];
                const title = modules.find((mod) => mod.key === key)?.title || key;
                return (
                  <li key={key}>
                    <span>{key}. {title}</span>
                    <span>{STATUS_LABELS[row.status]} · {row.percent}%</span>
                  </li>
                );
              })}
            </ul>
            <p className="survey-admin-activity">
              Last activity {activityDate(person.lastActivityAt) || 'none yet'}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

const SurveyProgressPage = () => {
  const [roster, setRoster] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    get('/api/leaders-survey/roster')
      .then((response) => {
        if (cancelled) return;
        if (!response.ok) {
          setError(getErrorMessage(response, 'Failed to load progress'));
          setLoading(false);
          return;
        }
        setRoster(response.data);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load progress');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <div className="survey-admin"><p>Loading…</p></div>;
  if (error) {
    return (
      <div className="survey-admin">
        <PageHeader title="Survey progress" section="leaders" />
        <p className="survey-admin-error" role="alert">{error}</p>
      </div>
    );
  }
  return <SurveyProgressView roster={roster} />;
};

export default SurveyProgressPage;
